
import React, { useEffect } from 'react';
import { CheckCircle, X, AlertTriangle, Info, XCircle } from 'lucide-react';
import CloseButton from './elements/CloseButton';


const typeConfig = {
  success: {
    icon: <CheckCircle className="w-6 h-6 text-white" />, label: 'Success',
    bg: 'from-accent-main to-green-500', border: 'border-accent-main',
    labelColor: 'text-accent-main',
  },
  error: {
    icon: <XCircle className="w-6 h-6 text-white" />, label: 'Error',
    bg: 'from-red-600 to-red-400', border: 'border-red-600',
    labelColor: 'text-red-600',
  },
  warning: {
    icon: <AlertTriangle className="w-6 h-6 text-white" />, label: 'Warning',
    bg: 'from-yellow-400 to-orange-500', border: 'border-yellow-500',
    labelColor: 'text-yellow-700',
  },
  info: {
    icon: <Info className="w-6 h-6 text-white" />, label: 'Info',
    bg: 'from-accent-main to-primary-dark', border: 'border-accent-main',
    labelColor: 'text-primary-dark',
  },
};

const Notification = ({ message, onClose, duration = 5000, type = 'success' }) => {
  const [timestamp, setTimestamp] = React.useState(null);
  const config = typeConfig[type] || typeConfig.success;

  useEffect(() => {
    if (!message) return;
    setTimestamp(new Date());
    const timer = setTimeout(() => {
      if (onClose) onClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [message, onClose, duration]);

  if (!message) return null;
  const formattedTime = timestamp ? timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
  return (
    // ABOVE EVERY DIALOG, which is what `z-[90]` is for and why it is not `z-50`.
    //
    // A toast is almost always ABOUT the dialog that is open: "Could not save this
    // nominee", "Failed to change password". It used to sit at z-50, the same layer every
    // modal scrim uses, and was rendered before the modal in its page's JSX - so equal
    // z-index left DOM order to decide, and the scrim painted over the message. The
    // operator saw their error greyed out behind a 70%-black veil, could not read it and
    // could not click its close button (the scrim swallowed the click). That is EL-32,
    // reported against Add Nominee and true of every page that shows a toast over a modal.
    //
    // The ladder, so a later overlay lands in the right place relative to this one:
    //   50   modal scrims and dialogs (Modal, ConfirmModal, the admin ones)
    //   80   the cookie-consent gate
    //   90   this - a message about the thing on top has to be on top of it
    //   100  the staging watermark, which must never be hidden by anything
    //   9999 the theme-change veil, a deliberate full-screen block for ~1s
    // Notification.zIndex.test.js enforces that nothing dialog-shaped creeps above 90.
    <div className={`fixed top-8 right-8 z-[90] min-w-[320px] max-w-xs flex items-start gap-4 bg-card-background border ${config.border} shadow-xl rounded-2xl px-5 py-4 animate-fade-in-out transition-all duration-500 font-sans`}> 
      <div className={`flex-shrink-0 rounded-full bg-gradient-to-br ${config.bg} p-2 shadow-md`}> 
        {config.icon} 
      </div> 
      <div className="flex-1"> 
        <span className={`block font-semibold text-base tracking-wide mb-1 ${config.labelColor}`}>{config.label}</span> 
        <span className="block text-primary-dark text-sm leading-snug">{message}</span> 
        {formattedTime && ( 
          <span className="block text-xs text-gray-400 mt-1">{formattedTime}</span> 
        )} 
      </div> 
      {onClose && ( 
        <CloseButton onClick={onClose} />
      )}
    </div> 
  );
};

export default Notification;
